import React, { useState } from 'react';
import { useStudentAuth } from '../../hooks/useStudentAuth';
import { supabase } from '../../lib/supabase';
import {
  User,
  Lock,
  CheckCircle,
  MapPin,
  Phone,
  Eye,
  EyeOff,
  AlertCircle,
  Mail,
  Shield,
  Calendar,
} from 'lucide-react';

const inputClass =
  'w-full px-4 py-3 border border-gray-200 dark:border-neutral-800 rounded-2xl focus:ring-2 focus:ring-[#970000]/10 focus:border-[#970000] outline-none transition-colors duration-200 bg-white dark:bg-neutral-900 text-gray-900 dark:text-neutral-100 text-sm';
const labelClass = 'block text-xs font-bold tracking-wide uppercase text-gray-400 dark:text-neutral-500 mb-1.5';

const COUNTRIES = [
  { code: 'DE', label: 'Deutschland' },
  { code: 'AT', label: 'Österreich' },
  { code: 'CH', label: 'Schweiz' },
  { code: 'LU', label: 'Luxemburg' },
  { code: 'LI', label: 'Liechtenstein' },
  { code: 'TR', label: 'Türkei' },
  { code: 'SA', label: 'Saudi-Arabien' },
  { code: 'AE', label: 'Vereinigte Arabische Emirate' },
  { code: 'EG', label: 'Ägypten' },
  { code: 'MA', label: 'Marokko' },
  { code: 'TN', label: 'Tunesien' },
  { code: 'DZ', label: 'Algerien' },
  { code: 'LB', label: 'Libanon' },
  { code: 'SY', label: 'Syrien' },
  { code: 'IQ', label: 'Irak' },
  { code: 'JO', label: 'Jordanien' },
  { code: 'NL', label: 'Niederlande' },
  { code: 'BE', label: 'Belgien' },
  { code: 'FR', label: 'Frankreich' },
  { code: 'GB', label: 'Vereinigtes Königreich' },
  { code: 'SE', label: 'Schweden' },
  { code: 'NO', label: 'Norwegen' },
  { code: 'DK', label: 'Dänemark' },
  { code: 'US', label: 'USA' },
  { code: 'CA', label: 'Kanada' },
  { code: 'AU', label: 'Australien' },
  { code: 'OTHER', label: 'Sonstiges' },
];

const POSTAL_PATTERNS: Record<string, { pattern: RegExp; hint: string }> = {
  DE: { pattern: /^\d{5}$/, hint: '5 Ziffern (z.B. 10115)' },
  AT: { pattern: /^\d{4}$/, hint: '4 Ziffern (z.B. 1010)' },
  CH: { pattern: /^\d{4}$/, hint: '4 Ziffern (z.B. 8001)' },
  LU: { pattern: /^\d{4}$/, hint: '4 Ziffern' },
  NL: { pattern: /^\d{4}\s?[A-Z]{2}$/i, hint: '4 Ziffern + 2 Buchstaben (z.B. 1234 AB)' },
  BE: { pattern: /^\d{4}$/, hint: '4 Ziffern' },
  FR: { pattern: /^\d{5}$/, hint: '5 Ziffern' },
  GB: { pattern: /^[A-Z]{1,2}\d[A-Z\d]?\s?\d[A-Z]{2}$/i, hint: 'z.B. SW1A 2AA' },
};

function getPasswordStrength(pw: string): { score: number; label: string; color: string } {
  let score = 0;
  if (pw.length >= 8) score++;
  if (pw.length >= 12) score++;
  if (/[A-Z]/.test(pw)) score++;
  if (/[0-9]/.test(pw)) score++;
  if (/[^A-Za-z0-9]/.test(pw)) score++;
  if (score <= 1) return { score, label: 'Sehr schwach', color: 'bg-[#970000]' };
  if (score === 2) return { score, label: 'Schwach', color: 'bg-[#c04000]' };
  if (score === 3) return { score, label: 'Mittel', color: 'bg-[#c8a030]' };
  if (score === 4) return { score, label: 'Stark', color: 'bg-[#5f8050]' };
  return { score, label: 'Sehr stark', color: 'bg-[#3a6040]' };
}

const FEEDBACK_TIMEOUT = 6000;

function SectionCard({
  icon: Icon,
  label,
  title,
  children,
}: {
  icon: React.ElementType;
  label: string;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="bg-white dark:bg-neutral-900 rounded-2xl border border-gray-200 dark:border-neutral-800 shadow-sm overflow-hidden">
      <div className="px-6 pt-5 pb-4 border-b border-gray-200 dark:border-neutral-800 flex items-center gap-3">
        <div className="w-9 h-9 rounded-xl bg-[#970000]/10 dark:bg-red-500/15 flex items-center justify-center shrink-0">
          <Icon className="h-4 w-4 text-[#970000] dark:text-red-400" />
        </div>
        <div>
          <p className="text-[10px] font-bold tracking-[0.14em] uppercase text-gray-400 dark:text-neutral-500">{label}</p>
          <h2 className="text-base font-bold text-gray-900 dark:text-neutral-100 leading-tight">{title}</h2>
        </div>
      </div>
      <div className="p-6">{children}</div>
    </div>
  );
}

function FeedbackSuccess({ message }: { message: string }) {
  return (
    <div className="flex items-center gap-2.5 text-sm text-gray-500 dark:text-neutral-400 bg-gray-50 dark:bg-neutral-800 border border-gray-200 dark:border-neutral-800 px-4 py-3 rounded-2xl">
      <CheckCircle className="h-4 w-4 shrink-0" />
      {message}
    </div>
  );
}

function FeedbackError({ message }: { message: string }) {
  return (
    <div className="flex items-start gap-2.5 text-sm text-[#970000] dark:text-red-400 bg-[#970000]/5 dark:bg-red-500/10 border border-[#970000]/20 dark:border-red-500/40 px-4 py-3 rounded-2xl">
      <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
      <p>{message}</p>
    </div>
  );
}

const StudentProfilePage: React.FC = () => {
  const { user, profile, updateProfile } = useStudentAuth();

  const [firstName, setFirstName] = useState(profile?.first_name ?? '');
  const [lastName, setLastName] = useState(profile?.last_name ?? '');
  const [displayName, setDisplayName] = useState(profile?.display_name ?? '');
  const [savingProfile, setSavingProfile] = useState(false);
  const [profileSaved, setProfileSaved] = useState(false);
  const [profileError, setProfileError] = useState('');

  const [phone, setPhone] = useState(profile?.phone ?? '');
  const [street, setStreet] = useState(profile?.street ?? '');
  const [houseNumber, setHouseNumber] = useState(profile?.house_number ?? '');
  const [postalCode, setPostalCode] = useState(profile?.postal_code ?? '');
  const [city, setCity] = useState(profile?.city ?? '');
  const [country, setCountry] = useState(profile?.country ?? 'DE');
  const [savingBilling, setSavingBilling] = useState(false);
  const [billingSaved, setBillingSaved] = useState(false);
  const [billingError, setBillingError] = useState('');

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPw, setShowNewPw] = useState(false);
  const [showConfirmPw, setShowConfirmPw] = useState(false);
  const [confirmPasswordChange, setConfirmPasswordChange] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  const [passwordSaved, setPasswordSaved] = useState(false);
  const [passwordError, setPasswordError] = useState('');

  const handleSaveProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    setProfileError('');
    setProfileSaved(false);
    if (!firstName.trim() || !lastName.trim()) {
      setProfileError('Vor- und Nachname dürfen nicht leer sein.');
      return;
    }
    if (firstName.trim().length > 100 || lastName.trim().length > 100) {
      setProfileError('Name darf nicht länger als 100 Zeichen sein.');
      return;
    }
    setSavingProfile(true);
    try {
      await updateProfile({
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        display_name: displayName.trim() || null,
      });
      setProfileSaved(true);
      setTimeout(() => setProfileSaved(false), FEEDBACK_TIMEOUT);
    } catch (err: unknown) {
      setProfileError(err instanceof Error ? err.message : 'Fehler beim Speichern');
    } finally {
      setSavingProfile(false);
    }
  };

  const handleSaveBilling = async (e: React.FormEvent) => {
    e.preventDefault();
    setBillingError('');
    setBillingSaved(false);
    const postalRule = POSTAL_PATTERNS[country];
    if (postalCode && postalRule && !postalRule.pattern.test(postalCode.trim())) {
      setBillingError(`Ungültige PLZ für das gewählte Land. Erwartet: ${postalRule.hint}`);
      return;
    }
    setSavingBilling(true);
    try {
      await updateProfile({
        phone: phone.trim() || null,
        street: street.trim() || null,
        house_number: houseNumber.trim() || null,
        postal_code: postalCode.trim() || null,
        city: city.trim() || null,
        country,
      });
      setBillingSaved(true);
      setTimeout(() => setBillingSaved(false), FEEDBACK_TIMEOUT);
    } catch (err: unknown) {
      setBillingError(err instanceof Error ? err.message : 'Fehler beim Speichern');
    } finally {
      setSavingBilling(false);
    }
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordSaved(false);
    const strength = getPasswordStrength(newPassword);
    if (newPassword.length < 8) {
      setPasswordError('Passwort muss mindestens 8 Zeichen lang sein.');
      return;
    }
    if (strength.score < 2) {
      setPasswordError('Passwort ist zu schwach. Füge Großbuchstaben oder Zahlen hinzu.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError('Passwörter stimmen nicht überein.');
      return;
    }
    if (!confirmPasswordChange) {
      setConfirmPasswordChange(true);
      return;
    }
    setSavingPassword(true);
    try {
      const { error } = await supabase.auth.updateUser({ password: newPassword });
      if (error) throw error;
      setPasswordSaved(true);
      setNewPassword('');
      setConfirmPassword('');
      setConfirmPasswordChange(false);
      setTimeout(() => setPasswordSaved(false), FEEDBACK_TIMEOUT);
    } catch (err: unknown) {
      setPasswordError(err instanceof Error ? err.message : 'Fehler beim Ändern des Passworts');
    } finally {
      setSavingPassword(false);
    }
  };

  const pwStrength = newPassword ? getPasswordStrength(newPassword) : null;
  const roleLabel: Record<string, string> = { admin: 'Administrator', student: 'Schüler' };
  const fullName = [profile?.first_name, profile?.last_name].filter(Boolean).join(' ') || profile?.display_name || 'Schüler';
  const initials = [profile?.first_name?.[0], profile?.last_name?.[0]].filter(Boolean).join('').toUpperCase() || '?';

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div>
        <p className="text-sm font-semibold tracking-[0.12em] uppercase text-[#970000]/70 dark:text-red-400 mb-1">Konto</p>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-neutral-100">Mein Profil</h1>
      </div>

      {/* Avatar hero strip */}
      <div className="bg-white dark:bg-neutral-900 rounded-2xl border border-gray-200 dark:border-neutral-800 shadow-sm p-5 flex items-center gap-4">
        <div className="w-14 h-14 rounded-2xl bg-[#970000] flex items-center justify-center shrink-0 shadow-[0_4px_14px_rgba(151,0,0,0.25)]">
          <span className="text-white text-lg font-bold tracking-wide">{initials}</span>
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-bold text-gray-900 dark:text-neutral-100 text-base leading-tight">{fullName}</p>
          <p className="text-sm text-gray-400 dark:text-neutral-500 mt-0.5">{user?.email}</p>
        </div>
        <span className={`shrink-0 px-2.5 py-1 rounded-full text-xs font-semibold ${
          profile?.is_active
            ? 'bg-gray-50 dark:bg-neutral-800 text-gray-500 dark:text-neutral-400 border border-gray-200 dark:border-neutral-800'
            : 'bg-[#970000]/10 dark:bg-red-500/15 text-[#970000] dark:text-red-400'
        }`}>
          {profile?.is_active ? 'Aktiv' : 'Inaktiv'}
        </span>
      </div>

      {/* Personal info */}
      <SectionCard icon={User} label="Profil" title="Persönliche Daten">
        <form onSubmit={handleSaveProfile} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Vorname</label>
              <input
                type="text"
                value={firstName}
                onChange={e => setFirstName(e.target.value)}
                className={inputClass}
                maxLength={100}
                required
              />
            </div>
            <div>
              <label className={labelClass}>Nachname</label>
              <input
                type="text"
                value={lastName}
                onChange={e => setLastName(e.target.value)}
                className={inputClass}
                maxLength={100}
                required
              />
            </div>
          </div>
          <div>
            <label className={labelClass}>
              Anzeigename <span className="text-gray-300 dark:text-neutral-600 normal-case font-normal">(optional)</span>
            </label>
            <input
              type="text"
              value={displayName}
              onChange={e => setDisplayName(e.target.value)}
              className={inputClass}
              placeholder="Wie soll dein Name angezeigt werden?"
              maxLength={100}
            />
          </div>
          <div>
            <label className={labelClass}>E-Mail</label>
            <div className="relative">
              <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-300 dark:text-neutral-600" />
              <input
                type="email"
                value={user?.email ?? ''}
                disabled
                className={inputClass + ' pl-10 opacity-60 cursor-not-allowed bg-gray-50 dark:bg-neutral-800'}
              />
            </div>
            <p className="text-xs text-gray-300 dark:text-neutral-600 mt-1.5">Die E-Mail-Adresse kann nicht geändert werden.</p>
          </div>

          {profileError && <FeedbackError message={profileError} />}
          {profileSaved && <FeedbackSuccess message="Profil gespeichert" />}

          <div className="pt-1">
            <button
              type="submit"
              disabled={savingProfile}
              className="px-5 py-2.5 bg-[#970000] hover:bg-[#7a0000] text-white text-sm font-semibold rounded-xl transition-colors duration-200 disabled:opacity-60"
            >
              {savingProfile ? 'Wird gespeichert…' : 'Speichern'}
            </button>
          </div>
        </form>
      </SectionCard>

      {/* Billing address */}
      <SectionCard icon={MapPin} label="Adresse" title="Rechnungsadresse">
        <p className="text-sm text-gray-400 dark:text-neutral-500 mb-5 -mt-1">
          Wird für Rechnungen und Zahlungsbelege verwendet.
        </p>
        <form onSubmit={handleSaveBilling} className="space-y-4">
          <div>
            <label className={labelClass}>
              Telefonnummer <span className="text-gray-300 dark:text-neutral-600 normal-case font-normal">(optional)</span>
            </label>
            <div className="relative">
              <Phone className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-300 dark:text-neutral-600" />
              <input
                type="tel"
                value={phone}
                onChange={e => setPhone(e.target.value)}
                className={inputClass + ' pl-10'}
                placeholder="+49 123 456789"
                maxLength={30}
              />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2">
              <label className={labelClass}>Straße</label>
              <input
                type="text"
                value={street}
                onChange={e => setStreet(e.target.value)}
                className={inputClass}
                placeholder="Musterstraße"
                maxLength={150}
              />
            </div>
            <div>
              <label className={labelClass}>Hausnr.</label>
              <input
                type="text"
                value={houseNumber}
                onChange={e => setHouseNumber(e.target.value)}
                className={inputClass}
                placeholder="12a"
                maxLength={20}
              />
            </div>
          </div>

          <div>
            <label className={labelClass}>Land</label>
            <select
              value={country}
              onChange={e => { setCountry(e.target.value); setPostalCode(''); }}
              className={inputClass}
            >
              {COUNTRIES.map(c => (
                <option key={c.code} value={c.code}>{c.label}</option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className={labelClass}>PLZ</label>
              <input
                type="text"
                value={postalCode}
                onChange={e => setPostalCode(e.target.value)}
                className={inputClass}
                placeholder={POSTAL_PATTERNS[country]?.hint.split('(')[0].trim() ?? 'PLZ'}
                maxLength={12}
              />
              {POSTAL_PATTERNS[country] && (
                <p className="text-xs text-gray-300 dark:text-neutral-600 mt-1">{POSTAL_PATTERNS[country].hint}</p>
              )}
            </div>
            <div className="col-span-2">
              <label className={labelClass}>Stadt</label>
              <input
                type="text"
                value={city}
                onChange={e => setCity(e.target.value)}
                className={inputClass}
                placeholder="Berlin"
                maxLength={100}
              />
            </div>
          </div>

          {billingError && <FeedbackError message={billingError} />}
          {billingSaved && <FeedbackSuccess message="Adresse gespeichert" />}

          <div className="pt-1">
            <button
              type="submit"
              disabled={savingBilling}
              className="px-5 py-2.5 bg-[#970000] hover:bg-[#7a0000] text-white text-sm font-semibold rounded-xl transition-colors duration-200 disabled:opacity-60"
            >
              {savingBilling ? 'Wird gespeichert…' : 'Adresse speichern'}
            </button>
          </div>
        </form>
      </SectionCard>

      {/* Password */}
      <SectionCard icon={Lock} label="Sicherheit" title="Passwort ändern">
        <form onSubmit={handleChangePassword} className="space-y-4">
          <div>
            <label className={labelClass}>Neues Passwort</label>
            <div className="relative">
              <input
                type={showNewPw ? 'text' : 'password'}
                value={newPassword}
                onChange={e => { setNewPassword(e.target.value); setConfirmPasswordChange(false); }}
                className={inputClass + ' pr-12'}
                placeholder="Mindestens 8 Zeichen"
                minLength={8}
                required
                autoComplete="new-password"
              />
              <button
                type="button"
                onClick={() => setShowNewPw(v => !v)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-300 dark:text-neutral-600 hover:text-gray-500 dark:hover:text-neutral-300 transition-colors"
                tabIndex={-1}
              >
                {showNewPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            {pwStrength && (
              <div className="mt-2.5 space-y-1.5">
                <div className="flex gap-1">
                  {[1, 2, 3, 4, 5].map(i => (
                    <div
                      key={i}
                      className={`h-1 flex-1 rounded-full transition-all duration-300 ${
                        i <= pwStrength.score ? pwStrength.color : 'bg-gray-200 dark:bg-neutral-800'
                      }`}
                    />
                  ))}
                </div>
                <p className="text-xs text-gray-400 dark:text-neutral-500">{pwStrength.label}</p>
              </div>
            )}
          </div>

          <div>
            <label className={labelClass}>Passwort bestätigen</label>
            <div className="relative">
              <input
                type={showConfirmPw ? 'text' : 'password'}
                value={confirmPassword}
                onChange={e => { setConfirmPassword(e.target.value); setConfirmPasswordChange(false); }}
                className={inputClass + ' pr-12'}
                placeholder="Passwort wiederholen"
                required
                autoComplete="new-password"
              />
              <button
                type="button"
                onClick={() => setShowConfirmPw(v => !v)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-300 dark:text-neutral-600 hover:text-gray-500 dark:hover:text-neutral-300 transition-colors"
                tabIndex={-1}
              >
                {showConfirmPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>

          {confirmPasswordChange && !passwordError && (
            <div className="flex items-start gap-3 p-4 bg-gray-50 dark:bg-neutral-800 border border-gray-200 dark:border-neutral-800 rounded-2xl">
              <AlertCircle className="h-4 w-4 text-gray-400 dark:text-neutral-500 shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-gray-900 dark:text-neutral-100">Passwort wirklich ändern?</p>
                <p className="text-xs text-gray-500 dark:text-neutral-400 mt-0.5">Du bleibst nach der Änderung eingeloggt.</p>
              </div>
            </div>
          )}

          {passwordError && <FeedbackError message={passwordError} />}
          {passwordSaved && <FeedbackSuccess message="Passwort wurde geändert" />}

          <div className="pt-1">
            <button
              type="submit"
              disabled={savingPassword}
              className={`px-5 py-2.5 text-white text-sm font-semibold rounded-xl transition-colors duration-200 disabled:opacity-60 ${
                confirmPasswordChange ? 'bg-gray-500 hover:bg-gray-700' : 'bg-[#970000] hover:bg-[#7a0000]'
              }`}
            >
              {savingPassword
                ? 'Wird gespeichert…'
                : confirmPasswordChange
                  ? 'Ja, Passwort ändern'
                  : 'Passwort ändern'}
            </button>
          </div>
        </form>
      </SectionCard>

      {/* Account meta */}
      <div className="bg-white dark:bg-neutral-900 rounded-2xl border border-gray-200 dark:border-neutral-800 shadow-sm overflow-hidden">
        <div className="px-6 pt-5 pb-4 border-b border-gray-200 dark:border-neutral-800 flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-[#970000]/10 dark:bg-red-500/15 flex items-center justify-center shrink-0">
            <Shield className="h-4 w-4 text-[#970000] dark:text-red-400" />
          </div>
          <div>
            <p className="text-[10px] font-bold tracking-[0.14em] uppercase text-gray-400 dark:text-neutral-500">Konto</p>
            <h2 className="text-base font-bold text-gray-900 dark:text-neutral-100 leading-tight">Konto-Informationen</h2>
          </div>
        </div>
        <div className="divide-y divide-[#f3ede8] dark:divide-neutral-800">
          <div className="flex items-center justify-between px-6 py-3.5">
            <div className="flex items-center gap-2.5">
              <User className="h-3.5 w-3.5 text-gray-300 dark:text-neutral-600" />
              <span className="text-sm text-gray-500 dark:text-neutral-400">Rolle</span>
            </div>
            <span className="text-sm font-semibold text-gray-900 dark:text-neutral-100">
              {roleLabel[profile?.role ?? ''] ?? (profile?.role ?? '–')}
            </span>
          </div>
          <div className="flex items-center justify-between px-6 py-3.5">
            <div className="flex items-center gap-2.5">
              <Calendar className="h-3.5 w-3.5 text-gray-300 dark:text-neutral-600" />
              <span className="text-sm text-gray-500 dark:text-neutral-400">Registriert seit</span>
            </div>
            <span className="text-sm font-semibold text-gray-900 dark:text-neutral-100">
              {profile?.created_at
                ? new Date(profile.created_at).toLocaleDateString('de-DE', {
                    day: '2-digit', month: 'long', year: 'numeric',
                  })
                : '–'}
            </span>
          </div>
          <div className="flex items-center justify-between px-6 py-3.5">
            <div className="flex items-center gap-2.5">
              <Shield className="h-3.5 w-3.5 text-gray-300 dark:text-neutral-600" />
              <span className="text-sm text-gray-500 dark:text-neutral-400">Status</span>
            </div>
            <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${
              profile?.is_active
                ? 'bg-gray-50 dark:bg-neutral-800 text-gray-500 dark:text-neutral-400 border border-gray-200 dark:border-neutral-800'
                : 'bg-[#970000]/10 dark:bg-red-500/15 text-[#970000] dark:text-red-400'
            }`}>
              {profile?.is_active ? 'Aktiv' : 'Inaktiv'}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StudentProfilePage;
